import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, ExternalLink, Mail, Calendar, Percent, DollarSign } from "lucide-react";
import { format, parseISO } from "date-fns";

interface Invoice {
  id: string;
  payment_amount: number;
  payment_due_date: string;
  supplier_name: string;
  supplier_email: string;
  status: string;
  apr_rate: number;
  discount_amount: number | null;
  discounted_payment_amount: number | null;
  email_sent_at: string | null;
  email_error: string | null;
  created_at: string;
  invoice_file_url?: string | null;
  invoice_file_name?: string | null;
}

interface InvoiceDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice | null;
}

export function InvoiceDetailModal({ open, onOpenChange, invoice }: InvoiceDetailModalProps) {
  if (!invoice) return null;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch {
      return dateString;
    }
  };

  const statusLabel: Record<string, string> = {
    pending: "Pending",
    early_payment_scheduled: "Scheduled",
    early_payment_completed: "Completed",
    standard_payment: "No Agreement",
    early_payment: "Scheduled",
    paid: "Completed",
  };

  const statusColor: Record<string, string> = {
    pending: "bg-warning/10 text-warning border-warning/20",
    early_payment_scheduled: "bg-success/10 text-success border-success/20",
    early_payment_completed: "bg-primary/10 text-primary border-primary/20",
    standard_payment: "bg-muted text-muted-foreground border-border",
    early_payment: "bg-success/10 text-success border-success/20",
    paid: "bg-primary/10 text-primary border-primary/20",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Invoice Details
          </DialogTitle>
          <DialogDescription>{invoice.supplier_name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Status</span>
            <Badge className={`${statusColor[invoice.status] || ""} border`}>
              {statusLabel[invoice.status] || invoice.status}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <DollarSign className="w-3 h-3" /> Original Amount
              </p>
              <p className="font-semibold">{formatCurrency(invoice.payment_amount)}</p>
            </div>
            {invoice.discounted_payment_amount && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Discounted Amount
                </p>
                <p className="font-semibold text-success">
                  {formatCurrency(invoice.discounted_payment_amount)}
                </p>
              </div>
            )}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Due Date
              </p>
              <p className="font-semibold">{formatDate(invoice.payment_due_date)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Percent className="w-3 h-3" /> APR Rate
              </p>
              <p className="font-semibold">{invoice.apr_rate}%</p>
            </div>
            {invoice.discount_amount && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Discount Saved</p>
                <p className="font-semibold text-success">
                  {formatCurrency(invoice.discount_amount)}
                </p>
              </div>
            )}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Mail className="w-3 h-3" /> Supplier Email
              </p>
              <p className="text-sm truncate">{invoice.supplier_email}</p>
            </div>
          </div>

          {invoice.email_sent_at && (
            <p className="text-xs text-muted-foreground">
              Email sent: {formatDate(invoice.email_sent_at)}
            </p>
          )}

          {invoice.email_error && (
            <p className="text-xs text-destructive">Email error: {invoice.email_error}</p>
          )}

          <p className="text-xs text-muted-foreground">Created: {formatDate(invoice.created_at)}</p>

          {invoice.invoice_file_url && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.open(invoice.invoice_file_url!, "_blank")}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Original Invoice PDF
            </Button>
          )}

          {!invoice.invoice_file_url && (
            <p className="text-xs text-muted-foreground text-center py-2">
              No PDF attached to this invoice
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
